import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Descriptions, Popconfirm, Progress, Row, Segmented, Space, Table, Tag, Typography } from 'antd';
import { CheckCircleOutlined, DownloadOutlined, ExclamationCircleOutlined, MergeCellsOutlined, PauseCircleOutlined, RedoOutlined, ReloadOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';
import { CardTitle, StatCard } from '../../components/StatCard';
import { importTargetLabel } from '@crm/shared';
import type { ImportRowItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { downloadCsv } from '../../components/export-csv';
import { useImport, useImportProcessingStatus, useImportRows, useImportTargets, usePauseImport, useRerunImport, useResumeImport } from '../../features/imports/api';
import { errorMessage } from '../../api/client';
import { PageSkeleton, RecordError, tableLocale, tableLoading } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';
import { IMPORT_STATUS, processedCount } from './ImportsPage';

const RESULT_TAG: Record<string, { color: string; label: string }> = {
  created: { color: 'success', label: 'Created' },
  updated: { color: 'processing', label: 'Updated' },
  merged: { color: 'processing', label: 'Merged' },
  skipped: { color: 'default', label: 'Skipped' },
  error: { color: 'error', label: 'Error' },
  processing: { color: 'processing', label: 'In progress' },
};

/** Same threshold as the API: no heartbeat for this long while processing means the run died. */
const STALE_MS = 5 * 60_000;

export function ImportJobPage() {
  const { id } = useParams();
  const { message } = App.useApp();
  const job = useImport(id);
  const resume = useResumeImport();
  const rerun = useRerunImport();
  const pause = usePauseImport();
  const status = useImportProcessingStatus(job.data?.status === 'PROCESSING');
  const targets = useImportTargets(job.data?.objectType ?? 'CONTACT');
  const labelFor = (key: string) => {
    const t = targets.data?.find((x) => x.key === key);
    return t ? importTargetLabel(t) : key;
  };
  const [result, setResult] = useState('all');
  const [page, setPage] = useState(1);
  const rows = useImportRows(id, result, page, job.data ? `${job.data.status}-${processedCount(job.data)}` : undefined);

  if (job.isError) return <RecordError error={job.error} what="Import" backTo="/tools/imports" backLabel="All imports" onRetry={job.refetch} />;
  if (job.isLoading || !job.data) return <PageSkeleton />;
  const j = job.data;
  // not started yet: continue in the wizard
  if (j.status === 'UPLOADED') return <Navigate to={`/tools/imports/new?job=${j.id}`} replace />;
  const done = processedCount(j);
  const percent = j.totalRows ? Math.round((done / j.totalRows) * 100) : 100;
  const isContact = j.objectType === 'CONTACT';
  const quietFor = Date.now() - new Date(j.heartbeatAt ?? j.updatedAt).getTime();
  const stalled = j.status === 'PROCESSING' && quietFor > STALE_MS;
  // why a processing import may not move: the worker is down, or it has not picked this import up yet
  const st = status.data;
  const workerDown = j.status === 'PROCESSING' && !!st && (!st.workerSeenAt || Date.now() - new Date(st.workerSeenAt).getTime() > st.workerSeenWithinSeconds * 1000);
  const waiting = j.status === 'PROCESSING' && !j.heartbeatAt && !workerDown;
  const busyWith = (st?.active ?? []).filter((a) => a.id !== j.id);
  // a paused import whose run has not stopped yet (it clears its heartbeat when it does)
  const pausing = j.status === 'PAUSED' && !!j.heartbeatAt && new Date(j.heartbeatAt).getTime() > 0 && quietFor <= STALE_MS;
  // processing rows again is only offered when no run is alive
  const canRerun = j.status === 'COMPLETED' || j.status === 'FAILED' || (j.status === 'PAUSED' && !pausing) || stalled;
  const problems = j.problemRows ?? 0;
  const runAgain = (scope: 'all' | 'problems') =>
    rerun
      .mutateAsync({ id: j.id, scope })
      .then(() => {
        message.success(scope === 'all' ? 'Processing every row again' : `Processing ${plural(problems, 'row')} again`);
        setResult('all');
        setPage(1);
      })
      .catch((e) => message.error(errorMessage(e)));
  const resumeButton = (
    <Button type="primary" icon={<ReloadOutlined />} loading={resume.isPending} onClick={() => resume.mutateAsync(j.id).then(() => message.success('Import resumed')).catch((e) => message.error(errorMessage(e)))}>
      Resume import
    </Button>
  );

  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Importer', to: '/tools/imports' }, { title: j.filename }]}
        title={j.filename}
        subtitle={<Space><Tag color={IMPORT_STATUS[j.status].color}>{IMPORT_STATUS[j.status].label}</Tag><span>{isContact ? 'Contacts' : 'Organizations'} · uploaded {fmtDateTime(j.createdAt)}{j.createdBy ? ` by ${j.createdBy.name}` : ''}</span></Space>}
        extra={
          <Space wrap>
            {j.status === 'PROCESSING' && !stalled && (
              <Button icon={<PauseCircleOutlined />} loading={pause.isPending} onClick={() => pause.mutateAsync(j.id).then(() => message.success('Pausing after the current row')).catch((e) => message.error(errorMessage(e)))}>
                Pause
              </Button>
            )}
            {canRerun && (
              <Popconfirm
                title={`Process ${plural(problems, 'row')} again?`}
                description={<div style={{ maxWidth: 320 }}>Rows that failed, finished with a warning (such as a photo that was not stored) or never finished are imported again. Rows that went through are left as they are.</div>}
                okText="Retry"
                onConfirm={() => runAgain('problems')}
                disabled={!problems}
              >
                <Button icon={<RedoOutlined />} disabled={!problems} loading={rerun.isPending && rerun.variables?.scope === 'problems'}>
                  Retry problem rows{problems ? ` (${problems.toLocaleString()})` : ''}
                </Button>
              </Popconfirm>
            )}
            {canRerun && (
              <Popconfirm
                title={`Process all ${plural(j.totalRows, 'row')} again?`}
                description={<div style={{ maxWidth: 320 }}>Every row is imported again from the start. Records that already exist are matched{isContact ? ' by email' : ' by domain or name'} and only their empty fields are filled, so nothing is duplicated.</div>}
                okText="Re-run"
                onConfirm={() => runAgain('all')}
              >
                <Button icon={<ReloadOutlined />} loading={rerun.isPending && rerun.variables?.scope === 'all'}>
                  Re-run from the start
                </Button>
              </Popconfirm>
            )}
            <Button icon={<DownloadOutlined />} disabled={!j.errorCount} onClick={() => downloadCsv(`/imports/${j.id}/errors.csv`, {}).catch((e) => message.error(errorMessage(e)))}>
              Download error report
            </Button>
          </Space>
        }
      />
      {j.status === 'FAILED' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title="The import stopped"
          description={`${j.error ?? 'Unknown error'}. Resuming continues with the rows not imported yet.`}
          action={resumeButton}
        />
      )}
      {j.status === 'PAUSED' && (
        <Alert
          type={pausing ? 'info' : 'warning'}
          showIcon
          style={{ marginBottom: 16 }}
          title={pausing ? 'Pausing after the current row…' : `Paused at ${done.toLocaleString()} of ${plural(j.totalRows, 'row')}`}
          description={pausing ? 'The rows in progress are finished first; the rest stay untouched until you resume.' : 'Nothing is imported until you resume. Resuming continues with the rows not imported yet.'}
          action={pausing ? undefined : resumeButton}
        />
      )}
      {workerDown && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title="The background worker is not running"
          description={`Imports are processed by the background worker, which ${st?.workerSeenAt ? `last checked in ${fmtDateTime(st.workerSeenAt)}` : 'has never checked in'}. This import continues once it is back. On Render, open the service's Logs and look for lines starting with “[launcher] worker exited” to see why it stopped.`}
        />
      )}
      {waiting && !stalled && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Waiting for the worker to start this import"
          description={
            busyWith.length
              ? `The worker is busy with ${busyWith.map((a) => `“${a.filename}” (${(a.createdCount + a.updatedCount + a.mergedCount + a.skippedCount + a.errorCount).toLocaleString()} of ${a.totalRows.toLocaleString()} rows)`).join(' and ')}. This import starts as soon as a slot is free.`
              : 'It usually starts within a few seconds.'
          }
        />
      )}
      {stalled && !workerDown && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`No progress for ${Math.round(quietFor / 60_000)} minutes`}
          description="The server was probably restarted (for example by a deploy) while this import ran. It resumes on its own a few minutes after a restart, or you can resume it now. Rows already imported are not imported again."
          action={resumeButton}
        />
      )}
      <Card style={{ marginBottom: 24 }} title={<CardTitle title="Progress" subtitle={`${done.toLocaleString()} of ${plural(j.totalRows, 'row')} processed`} />}>
        <Progress percent={percent} status={j.status === 'FAILED' ? 'exception' : j.status === 'PROCESSING' ? 'active' : undefined} />
      </Card>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col flex="1 1 200px">
          <StatCard icon={<CheckCircleOutlined />} label="Created" value={j.createdCount.toLocaleString()} note={isContact ? 'New contacts' : 'New organizations'} />
        </Col>
        <Col flex="1 1 200px">
          <StatCard icon={<MergeCellsOutlined />} label={isContact ? 'Merged into existing' : 'Updated existing'} value={(isContact ? j.mergedCount : j.updatedCount).toLocaleString()} note={`${isContact ? 'Matched by email' : 'Matched by domain or name'}, ${j.mapping?.options.onDuplicate === 'replace' ? 'values replaced' : 'blanks filled'}`} />
        </Col>
        <Col flex="1 1 200px">
          <StatCard icon={<StopOutlined />} label="Skipped" value={j.skippedCount.toLocaleString()} note="Left unchanged" />
        </Col>
        <Col flex="1 1 200px">
          <StatCard
            icon={<WarningOutlined />}
            label="Imported with warnings"
            value={<span style={{ color: j.warningRows ? '#D97706' : undefined }}>{(j.warningRows ?? 0).toLocaleString()}</span>}
            note={j.warningRows ? 'Imported; some values were left out' : 'Every value was imported'}
          />
        </Col>
        <Col flex="1 1 200px">
          <StatCard
            icon={<ExclamationCircleOutlined />}
            label="Errors"
            value={<span style={{ color: j.errorCount ? '#E5484D' : undefined }}>{j.errorCount.toLocaleString()}</span>}
            note={j.errorCount ? 'Not imported; download the report to fix and re-upload' : 'Every row was imported'}
          />
        </Col>
      </Row>
      <Card title={<CardTitle title="Column mapping" subtitle="How each column in the file was imported" />} style={{ marginBottom: 24 }}>
        <Descriptions size="small" column={3} items={j.headers.map((h) => ({ key: h, label: h, children: j.mapping?.mapping[h] ? <Tag color="processing">{labelFor(j.mapping.mapping[h])}</Tag> : <Typography.Text type="secondary">Skipped</Typography.Text> }))} />
      </Card>
      <Card
        title={<CardTitle title="Rows" subtitle="Every row with its result" />}
        extra={<Segmented value={result} onChange={(v) => { setResult(String(v)); setPage(1); }} options={[{ value: 'all', label: 'All' }, { value: 'error', label: `Errors (${j.errorCount})` }, { value: 'warnings', label: `Warnings (${(j.warningRows ?? 0).toLocaleString()})` }, { value: 'created', label: 'Created' }, { value: isContact ? 'merged' : 'updated', label: isContact ? 'Merged' : 'Updated' }, { value: 'skipped', label: 'Skipped' }]} />}
      >
        <Table<ImportRowItem>
          rowKey="id"
          loading={tableLoading(rows)}
          locale={tableLocale(rows, result === 'all' ? 'No rows in this file.' : result === 'warnings' ? 'No rows imported with warnings.' : `No ${result === 'error' ? 'errors' : `${RESULT_TAG[result]?.label.toLowerCase() ?? result} rows`}.`)}
          scroll={{ x: 'max-content' }}
          dataSource={rows.data?.items ?? []}
          pagination={{ current: page, pageSize: 25, total: rows.data?.total ?? 0, onChange: setPage, showSizeChanger: false }}
          columns={[
            { title: 'Row', dataIndex: 'rowNumber', width: 70, fixed: 'left' },
            {
              title: 'Result',
              dataIndex: 'result',
              width: 170,
              fixed: 'left',
              // imported rows that left something out are flagged as warnings; errors are rows that were not imported
              render: (r: string | null, row: ImportRowItem) =>
                !r ? (
                  <Typography.Text type="secondary">Pending</Typography.Text>
                ) : r !== 'error' && r !== 'processing' && row.error ? (
                  <Tag color="warning">{RESULT_TAG[r]?.label ?? r} · warnings</Tag>
                ) : (
                  <Tag color={RESULT_TAG[r]?.color}>{RESULT_TAG[r]?.label ?? r}</Tag>
                ),
            },
            {
              title: 'Issue',
              dataIndex: 'error',
              width: 300,
              // errors stopped the row; anything else is a field value or image that was skipped while the row imported
              render: (e: string | null, r: ImportRowItem) => (e ? <Typography.Text type={r.result === 'error' ? 'danger' : 'warning'}>{e}</Typography.Text> : null),
            },
            ...j.headers.map((h) => ({ title: h, key: h, ellipsis: true, render: (_: unknown, r: ImportRowItem) => r.data[h] })),
          ]}
        />
      </Card>
      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
        Imported records carry an “Imported” entry in their history naming this file. Browse them in <Link to={isContact ? '/contacts' : '/organizations'}>{isContact ? 'Contacts' : 'Organizations'}</Link>.
      </Typography.Paragraph>
    </div>
  );
}
